// @flow

import React, { PureComponent } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import {
  Route,
  Switch,
  Redirect,
  type Match,
  type RouterHistory,
} from 'react-router-dom';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import moment from 'moment';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n, getCurrencySymbol } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Icon from 'shared/components/common/Icon';
import OrderStatusPill from 'shared/components/common/Pill/OrderStatusPill';
import UpdateOrderMutation from 'main-app/mutations/UpdateOrder';
import TabNav from 'shared/components/common/TabNav';
import Loader from 'shared/components/common/Loader';
import TabContentWrapper from 'shared/components/common/TabContent/Wrapper';
import MainContent from 'shared/components/common/TabContent/MainContent';
import AddUpdateOrderModal from 'main-app/components/AddUpdateOrderModal';
import DeleteOrderModal from 'main-app/components/DeleteOrderModal';
import AddUpdateInvoiceModal from 'main-app/components/AddUpdateInvoiceModal';
import AddUpdateShipmentModal from 'main-app/components/AddUpdateShipmentModal';
import EmailSalesOrderMutation from 'main-app/mutations/EmailSalesOrder';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import {
  PrintWrapper,
  PrintOnly,
  NoPrint,
  PrintPage,
  PrintDocHeader,
  PrintDocHeaderTitle,
  PrintDocHeaderCompany,
  PrintInfoGrid,
  PrintInfoColumn,
  PrintInfoRow,
  PrintInfoLabel,
  PrintNotes,
  PrintItemTable,
  PrintDocFooter,
  PrintSignatureGrid,
  PrintSignatureColumn,
  PrintSignatureLine,
  PrintSignatureUnderline,
} from 'shared/components/common/PrintLayout';
import OrderDetails from './OrderDetails';
import OrderAttachments from './OrderAttachments';
import OrderJobTickets from './OrderJobTickets';
import OrderSidePanel from './OrderSidePanel';

type Props = {
  history: RouterHistory,
  match: {
    ...Match,
    params: {
      id: string,
    },
  },
};

type State = {
  updateOrderModalOpen: boolean,
  deleteOrderModalOpen: boolean,
  invoiceModalOpen: boolean,
  shipmentModalOpen: boolean,
};

class Order extends PureComponent<Props, State> {
  menuTriggerRef = null;

  state = {
    updateOrderModalOpen: false,
    deleteOrderModalOpen: false,
    invoiceModalOpen: false,
    shipmentModalOpen: false,
  };

  handleOpenUpdateOrderModal = () => {
    this.setState({
      updateOrderModalOpen: true,
    });
  };

  handleOpenDeleteOrderModal = () => {
    this.setState({
      deleteOrderModalOpen: true,
    });
  };

  handleOpenInvoiceModal = () => {
    this.setState({ invoiceModalOpen: true });
  };

  handleOpenShipmentModal = () => {
    this.setState({ shipmentModalOpen: true });
  };

  handleDeleteSuccess = () => {
    const { history } = this.props;

    history.replace('/orders');
  };

  handleAdvanceStage = (orderId: string, newStatus: string) => {
    UpdateOrderMutation.commit({
      variables: { input: { id: orderId, status: newStatus } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Order status updated.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error updating order status.'), 'error');
      });
  };

  handleCloseAllModals = () => {
    this.setState({
      updateOrderModalOpen: false,
      deleteOrderModalOpen: false,
      invoiceModalOpen: false,
      shipmentModalOpen: false,
    });
  };

  handlePrintSalesOrder = () => {
    window.print();
  };

  handleEmailSalesOrder = (orderId: string) => {
    EmailSalesOrderMutation.commit({
      variables: { input: { orderId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Sales order email sent successfully.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error sending sales order email.'), 'error');
      });
  };

  render() {
    const { match, history } = this.props;
    const { updateOrderModalOpen, deleteOrderModalOpen, invoiceModalOpen, shipmentModalOpen } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query OrderQuery($id: ID!, $orderIds: [ID!]!) {
            order(id: $id) {
              id
              orderNumber
              customerOrderNumber
              status
              notes
              currency
              subtotal
              discountPercent
              discountAmount
              taxRate
              taxAmount
              totalAmount
              orderedAt
              promisedAt
              requiredAt
              createdAt
              shippingAddressLine1
              shippingAddressLine2
              shippingAddressLine3
              shippingAddressCity
              shippingAddressState
              shippingAddressZip
              yields(first: null) @connection(key: "Order_yields") {
                edges {
                  node {
                    id
                    orderedQuantity
                    unitPrice
                    discountPercent
                    lineTotal
                    item {
                      id
                      name
                      itemNumber
                      description
                      quantityUOM {
                        id
                        symbol
                      }
                    }
                  }
                }
              }
              customer {
                id
                name
                email
                phone
                billingAddressLine1
                billingAddressLine2
                billingAddressLine3
                billingAddressCity
                billingAddressState
                billingAddressZip
              }
              ...OrderSidePanel_order
              ...OrderDetails_order
              ...AddUpdateOrderModal_order
              ...DeleteOrderModal_order
              ...OrderJobTickets_order
            }
            jobs(first: null, orderIds: $orderIds)
              @connection(key: "Order_jobs", filters: ["orderIds"]) {
              edges {
                ...OrderJobTickets_jobEdges
              }
            }
            workflows(first: null) {
              edges {
                ...OrderJobTickets_workflowEdges
              }
            }
            viewer {
              id
              permissions
              company {
                id
                name
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
            }
            forms(types: [ITEM]) {
              edges {
                node {
                  id
                  type
                  ...OrderDetails_defaultForm
                }
              }
            }
          }
        `}
        variables={{
          id: match.params.id,
          orderIds: [match.params.id],
        }}
        render={query => {
          const order = query.props && query.props.order;
          const viewer = query.props && query.props.viewer;
          const yieldEdges =
            (order && order.yields && order.yields.edges) || [];

          const fmtCurrency = (n: ?number) => {
            const symbol = getCurrencySymbol(order && order.currency);
            const v = parseFloat(n || 0);
            return `${symbol}${v.toFixed(2)}`;
          };

          return (
            <Page>
              <PageHeader
                intro={i18n.t('ORDER')}
                title={
                  query.props
                    ? query.props.order.orderNumber
                    : i18n.t('Order Details Loading...')
                }
                right={
                  query.props && [
                    <OrderStatusPill status={query.props.order.status} />,
                    <ContextMenuTrigger
                      id="order-details-menu"
                      ref={r => (this.menuTriggerRef = r)}
                    >
                      <Icon
                        type="circle-context-menu"
                        onClick={e => {
                          if (this.menuTriggerRef) {
                            this.menuTriggerRef.handleContextClick(e);
                          }
                        }}
                      />
                    </ContextMenuTrigger>,
                  ]
                }
              />
              {!query.props ? (
                <Loader />
              ) : (
                <PrintWrapper>
                <PrintOnly>
                  <PrintPage>
                    <PrintDocHeader>
                      <PrintDocHeaderTitle>
                        <Logo style={{ width: 100, height: 'auto', display: 'block', marginBottom: 4 }} />
                        {i18n.t('Sales Order')}
                      </PrintDocHeaderTitle>
                      <PrintDocHeaderCompany>{viewer && viewer.company && viewer.company.name}</PrintDocHeaderCompany>
                    </PrintDocHeader>

                    <PrintInfoGrid>
                      <PrintInfoColumn>
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Order #')}</PrintInfoLabel>
                          <div>{order.orderNumber}</div>
                        </PrintInfoRow>
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Order Date')}</PrintInfoLabel>
                          <div>{order.orderedAt ? moment(order.orderedAt).format('MM/DD/YYYY') : moment(order.createdAt).format('MM/DD/YYYY')}</div>
                        </PrintInfoRow>
                        {order.promisedAt && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Promised Date')}</PrintInfoLabel>
                            <div>{moment(order.promisedAt).format('MM/DD/YYYY')}</div>
                          </PrintInfoRow>
                        )}
                        {order.requiredAt && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Required Date')}</PrintInfoLabel>
                            <div>{moment(order.requiredAt).format('MM/DD/YYYY')}</div>
                          </PrintInfoRow>
                        )}
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Customer')}</PrintInfoLabel>
                          <div>{order.customer.name}</div>
                        </PrintInfoRow>
                        {order.customerOrderNumber && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Customer PO')}</PrintInfoLabel>
                            <div>{order.customerOrderNumber}</div>
                          </PrintInfoRow>
                        )}
                        {(order.shippingAddressLine1 || order.shippingAddressCity) && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Ship To')}</PrintInfoLabel>
                            <div>
                              {[order.shippingAddressLine1, order.shippingAddressLine2, order.shippingAddressLine3].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
                              {(order.shippingAddressCity || order.shippingAddressState || order.shippingAddressZip) && (
                                <div>{[order.shippingAddressCity, order.shippingAddressState, order.shippingAddressZip].filter(Boolean).join(', ')}</div>
                              )}
                            </div>
                          </PrintInfoRow>
                        )}
                        {(order.customer.billingAddressLine1 || order.customer.billingAddressCity) && (
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Bill To')}</PrintInfoLabel>
                            <div>
                              {[order.customer.billingAddressLine1, order.customer.billingAddressLine2, order.customer.billingAddressLine3].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
                              {(order.customer.billingAddressCity || order.customer.billingAddressState || order.customer.billingAddressZip) && (
                                <div>{[order.customer.billingAddressCity, order.customer.billingAddressState, order.customer.billingAddressZip].filter(Boolean).join(', ')}</div>
                              )}
                            </div>
                          </PrintInfoRow>
                        )}
                      </PrintInfoColumn>
                      {viewer && viewer.company && (
                        <PrintInfoColumn>
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('From')}</PrintInfoLabel>
                            <div>{viewer.company.name}</div>
                          </PrintInfoRow>
                          {(viewer.company.addressLine1 || viewer.company.addressCity) && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Address')}</PrintInfoLabel>
                              <div>
                                {viewer.company.addressLine1 && <div>{viewer.company.addressLine1}</div>}
                                {viewer.company.addressLine2 && <div>{viewer.company.addressLine2}</div>}
                                {(viewer.company.addressCity || viewer.company.addressState || viewer.company.addressZip) && (
                                  <div>{[viewer.company.addressCity, viewer.company.addressState, viewer.company.addressZip].filter(Boolean).join(', ')}</div>
                                )}
                                {viewer.company.addressCountry && <div>{viewer.company.addressCountry}</div>}
                              </div>
                            </PrintInfoRow>
                          )}
                          {viewer.company.phone && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                              <div>{viewer.company.phone}</div>
                            </PrintInfoRow>
                          )}
                        </PrintInfoColumn>
                      )}
                    </PrintInfoGrid>

                    <PrintItemTable>
                      <thead>
                        <tr>
                          <th style={{ width: '5%' }}>#</th>
                          <th style={{ textAlign: 'left' }}>{i18n.t('Part #')}</th>
                          <th style={{ textAlign: 'left' }}>{i18n.t('Description')}</th>
                          <th>{i18n.t('Quantity')}</th>
                          <th style={{ textAlign: 'right' }}>{i18n.t('Unit Price')}</th>
                          <th style={{ textAlign: 'right' }}>{i18n.t('Line Total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yieldEdges.map((edge, idx) => (
                          <tr key={edge.node.id}>
                            <td>{idx + 1}</td>
                            <td>{edge.node.item.itemNumber || edge.node.item.name}</td>
                            <td>{edge.node.item.description || edge.node.item.name}</td>
                            <td style={{ textAlign: 'center' }}>{parseFloat(edge.node.orderedQuantity || 0).toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCurrency(edge.node.unitPrice)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtCurrency(edge.node.lineTotal)}</td>
                          </tr>
                        ))}
                        {yieldEdges.length === 0 && (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>{i18n.t('No items')}</td>
                          </tr>
                        )}
                      </tbody>
                    </PrintItemTable>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '16px 0 24px' }}>
                      <table style={{ borderSpacing: 0, minWidth: 280 }}>
                        <tbody>
                          {order.subtotal != null && (
                            <tr>
                              <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Subtotal:')}</td>
                              <td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtCurrency(order.subtotal)}</td>
                            </tr>
                          )}
                          {order.discountAmount != null && order.discountAmount > 0 && (
                            <tr>
                              <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Discount')}{order.discountPercent ? ` (${order.discountPercent}%)` : ''}:</td>
                              <td style={{ padding: '4px 0', textAlign: 'right' }}>-{fmtCurrency(order.discountAmount)}</td>
                            </tr>
                          )}
                          {order.taxAmount != null && order.taxAmount > 0 && (
                            <tr>
                              <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Tax')}{order.taxRate ? ` (${order.taxRate}%)` : ''}:</td>
                              <td style={{ padding: '4px 0', textAlign: 'right' }}>{fmtCurrency(order.taxAmount)}</td>
                            </tr>
                          )}
                          <tr>
                            <td style={{ fontWeight: 700, padding: '8px 16px 8px 0', textAlign: 'right', borderTop: '2px solid #333' }}>{i18n.t('Total:')}</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', borderTop: '2px solid #333', fontWeight: 700 }}>{fmtCurrency(order.totalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {order.notes && (
                      <PrintNotes>
                        <PrintInfoLabel>{i18n.t('Notes')}</PrintInfoLabel>
                        <div>{order.notes}</div>
                      </PrintNotes>
                    )}

                    <PrintSignatureGrid>
                      <PrintSignatureColumn>
                        <PrintSignatureLine>
                          <label>{i18n.t('Authorized by:')}</label>
                          <PrintSignatureUnderline />
                        </PrintSignatureLine>
                        <PrintSignatureLine>
                          <label>{i18n.t('Date:')}</label>
                          <PrintSignatureUnderline />
                        </PrintSignatureLine>
                      </PrintSignatureColumn>
                      <PrintSignatureColumn>
                        <PrintSignatureLine>
                          <label>{i18n.t('Customer signature:')}</label>
                          <PrintSignatureUnderline />
                        </PrintSignatureLine>
                        <PrintSignatureLine>
                          <label>{i18n.t('Date:')}</label>
                          <PrintSignatureUnderline />
                        </PrintSignatureLine>
                      </PrintSignatureColumn>
                    </PrintSignatureGrid>

                    <PrintDocFooter>
                      <span>{i18n.t('Created')} {moment(order.createdAt).format('MM/DD/YYYY')}</span>
                    </PrintDocFooter>
                  </PrintPage>
                </PrintOnly>

                <NoPrint>
                <>
                  <TabNav
                    history={history}
                    activePath={match.url}
                    tabs={[
                      {
                        label: i18n.t('Order Details'),
                        path: `/order/${match.params.id}`,
                      },
                      {
                        label: i18n.t('Job Tickets'),
                        path: `/order/${match.params.id}/job-tickets`,
                      },
                      {
                        label: i18n.t('Attachments'),
                        path: `/order/${match.params.id}/attachments`,
                      },
                    ]}
                  />
                  <TabContentWrapper>
                    <OrderSidePanel
                      order={query.props?.order}
                      customer={query.props?.order?.customer}
                      user={query.props.order.user}
                    />
                    <MainContent>
                      <Switch>
                        <Route
                          exact
                          path="/order/:id"
                          render={props => (
                            <OrderDetails
                              {...props}
                              history={history}
                              order={query.props?.order}
                              defaultForm={
                                query.props?.forms.edges[0]?.node || null
                              }
                            />
                          )}
                        />
                        <Route
                          exact
                          path="/order/:id/job-tickets"
                          render={props => (
                            <OrderJobTickets
                              {...props}
                              history={history}
                              order={query.props?.order}
                              jobEdges={query.props?.jobs?.edges}
                              workflowEdges={query.props?.workflows?.edges}
                              companyId={query.props?.viewer?.company.id}
                            />
                          )}
                        />
                        <Route
                          exact
                          path="/order/:id/attachments"
                          component={OrderAttachments}
                        />
                        <Route
                          exact
                          render={() => (
                            <Redirect to={`/order/${match.params.id}`} />
                          )}
                        />
                      </Switch>
                    </MainContent>
                  </TabContentWrapper>
                  <ContextMenu id="order-details-menu">
                    {query.props.viewer.permissions.includes(
                      'UPDATE_ORDER',
                    ) && (
                      <MenuItem onClick={this.handleOpenUpdateOrderModal}>
                        {i18n.t('Edit Order')}
                      </MenuItem>
                    )}
                    {query.props.viewer.permissions.includes('UPDATE_ORDER') &&
                      query.props.order.status === 'WAITING_FOR_CONFIRMATION' && (
                        <MenuItem onClick={() => this.handleAdvanceStage(query.props.order.id, 'CONFIRMED')}>
                          {i18n.t('Confirm Order')}
                        </MenuItem>
                      )}
                    {query.props.viewer.permissions.includes('UPDATE_ORDER') &&
                      query.props.order.status === 'CONFIRMED' && (
                        <MenuItem onClick={() => this.handleAdvanceStage(query.props.order.id, 'WAITING_FOR_PRODUCTION')}>
                          {i18n.t('Send to Production')}
                        </MenuItem>
                      )}
                    <MenuItem onClick={this.handleOpenDeleteOrderModal}>
                      {i18n.t('Delete Order')}
                    </MenuItem>
                    <MenuItem onClick={this.handleOpenInvoiceModal}>
                      {i18n.t('Create Invoice')}
                    </MenuItem>
                    <MenuItem onClick={this.handleOpenShipmentModal}>
                      {i18n.t('Create Shipment')}
                    </MenuItem>
                    <MenuItem onClick={this.handlePrintSalesOrder}>
                      {i18n.t('Print Sales Order')}
                    </MenuItem>
                    <MenuItem onClick={() => this.handleEmailSalesOrder(query.props.order.id)}>
                      {i18n.t('Email Sales Order')}
                    </MenuItem>
                  </ContextMenu>
                  {updateOrderModalOpen && (
                    <AddUpdateOrderModal
                      onClose={this.handleCloseAllModals}
                      order={query.props.order}
                      customer={null}
                    />
                  )}
                  {deleteOrderModalOpen && (
                    <DeleteOrderModal
                      history={history}
                      onClose={this.handleCloseAllModals}
                      order={query.props.order}
                      onSuccess={this.handleDeleteSuccess}
                    />
                  )}
                  {invoiceModalOpen && (
                    <AddUpdateInvoiceModal
                      onClose={this.handleCloseAllModals}
                      defaultOrder={query.props.order}
                      onSuccess={invoice => {
                        history.push(`/invoice/${invoice.id}`);
                      }}
                    />
                  )}
                  {shipmentModalOpen && (
                    <AddUpdateShipmentModal
                      onClose={this.handleCloseAllModals}
                      defaultOrder={query.props.order}
                      onSuccess={shipment => {
                        history.push(`/shipment/${shipment.id}`);
                      }}
                    />
                  )}
                </>
                </NoPrint>
                </PrintWrapper>
              )}
            </Page>
          );
        }}
      />
    );
  }
}

export default Order;
